"use client";
import Hyperlink from "@/components/ui/hyperlink";
import { useModalContext } from "@/contexts/ModalContext";
import Image from "next/image";
import { useEffect, useRef } from "react";

interface Props {
    title: string;
    description: string;
    subtext?: string;
    quote?: string;
    author?: string;
    btn?: string;
    btn1?: string;
    href?: string;
    target?: "_blank" | "_self";
    onModalOpen?: (modalTitle?: string) => void;
}

const Hero = ({
    title,
    description,
    btn,
    btn1,
    href,
    target = "_blank",
}: Props) => {
    const modal = useModalContext();
    const sectionRef = useRef<HTMLElement>(null);

    const handleModalOpen = (modalTitle?: string) => {
        modal.openModal(modalTitle);
    };

    useEffect(() => {
        const updateBackground = () => {
            if (sectionRef.current) {
                const element = sectionRef.current;
                const src = "/images/hero/1bfs_full.png";
                // We assume the class is always present as it's hardcoded in the JSX, 
                // but we keep the check to match existing logic style.
                const hasGradient = element.classList.contains('bg-color-gradient');
                const isLargeScreen = window.innerWidth >= 992; // Bootstrap lg breakpoint

                if (hasGradient) {
                    if (isLargeScreen) {
                        element.style.background = `
                            linear-gradient(63deg, rgba(255, 212, 163, 0.18) 17.08%, rgba(255, 255, 255, 0.18) 83.17%),
                            url(${src})
                        `;
                    } else {
                        element.style.background = `
                            linear-gradient(63deg, rgba(255, 212, 163, 0.18) 17.08%, rgba(255, 255, 255, 0.18) 83.17%)
                        `;
                    }
                    element.style.backgroundRepeat = 'no-repeat';
                    element.style.backgroundPositionX = 'right';
                    element.style.backgroundSize = 'cover';
                } else {
                    if (isLargeScreen) {
                        element.style.backgroundImage = `url(${src})`;
                    } else {
                        element.style.backgroundImage = 'none';
                    }
                }

                element.classList.add("background-image");
            }
        };

        // Initial check
        updateBackground();

        // Add listener
        window.addEventListener('resize', updateBackground);

        // Cleanup
        return () => {
            window.removeEventListener('resize', updateBackground);
        };
    }, []);

    return (
        <>
            <section
                ref={sectionRef}
                className="desilo-hero4-section bg-color-gradient section-padding-bottom overflow-hidden"
            >
                <div className="container custom-container">
                    <div className="row g-4 justify-content-between">
                        <div className="col-12 col-lg-7">
                            <div className="desilo-hero5__info">
                                <h1 className="title">
                                    {title}
                                </h1>
                                <p className="hero2-content-text text-color-primary">
                                    {description}
                                </p>
                                {btn && btn1 && (
                                    <div className="d-md-flex d-block align-items-center gap-4">
                                        <Hyperlink
                                            href={href || "https://calendly.com/parthik/30min"}
                                            variant="primary"
                                            target={target}
                                        >
                                            {btn}
                                        </Hyperlink>
                                        <Hyperlink
                                            variant="modal-pill"
                                            onModalOpen={handleModalOpen}
                                            modalTitle={btn1}
                                            className="text-color-primary d-block cursor-pointer"
                                        >
                                            {btn1}
                                        </Hyperlink>
                                    </div>
                                )}
                                {btn && !btn1 && (
                                    <Hyperlink
                                        href={href || "https://calendly.com/parthik/30min"}
                                        variant="primary"
                                        target={target}
                                    >
                                        {btn}
                                    </Hyperlink>
                                )}
                            </div>
                        </div>
                        <div className="col-lg-6 d-block d-lg-none">
                            <div className="ratio ratio-1x1 ratio-responsive">
                                <Image src="/images/hero/1bfs.png" className="ratio-img" alt="1bfs" width={500} height={500} />
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            {/* {subtext && quote && author && (
                <div className="container custom-container">
                    <div className="row section-margin-top gx-5">
                        <div className="col-12 quate-details-box">
                            <blockquote className="d-flex align-items-start gap-4 service-quote-section mb-0">
                                <svg xmlns="http://www.w3.org/2000/svg" width="42" height="34" viewBox="0 0 42 34" fill="none">
                                    <g clipPath="url(#clip0_3257_80348)">
                                        <mask id="mask0_3257_80348" style={{ maskType: "luminance" }} maskUnits="userSpaceOnUse" x="0" y="0" width="42" height="34">
                                            <path d="M42 33.5312L0 33.5312L0 0.53125L42 0.53125L42 33.5312Z" fill="white" />
                                        </mask>
                                        <g mask="url(#mask0_3257_80348)">
                                            <path d="M3.78651 3.40053C1.42857 6.03544 0.0138359 8.90987 0.0138359 13.7006C0.0138359 22.0844 5.9086 29.5101 14.1612 33.3426L16.2833 30.2287C8.50227 25.917 6.85173 20.4077 6.38019 16.8146C7.55913 17.5332 9.20961 17.7728 10.8602 17.5332C15.1044 17.0541 18.4055 13.7006 18.4055 9.14943C18.4055 6.99361 17.4623 4.83775 16.0475 3.161C14.397 1.48424 12.5107 0.765627 10.1528 0.765627C7.55913 0.765627 5.20119 1.96331 3.78651 3.40053ZM27.3654 3.40053C25.0076 6.03543 23.5928 8.90987 23.5928 13.7006C23.5928 22.0844 29.4875 29.5101 37.7402 33.3426L39.8623 30.2287C32.0812 25.917 30.4307 20.4076 29.9591 16.8146C31.1381 17.5332 32.7886 17.7728 34.4391 17.5332C38.6833 17.0541 41.9844 13.7006 41.9844 9.14943C41.9844 6.99361 41.0412 4.83775 39.6265 3.16099C38.2117 1.48424 36.0896 0.765624 33.7317 0.765625C31.1381 0.765625 28.7802 1.9633 27.3654 3.40053Z" fill="#E25926" />
                                        </g>
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_3257_80348">
                                            <rect width="42" height="33" fill="white" transform="matrix(-1 0 0 1 42 0.53125)" />
                                        </clipPath>
                                    </defs>
                                </svg>
                                <div>
                                    <div className="text">
                                        {quote}
                                    </div>
                                    <cite>
                                        ─ {author}
                                    </cite>
                                </div>
                            </blockquote>
                        </div>
                    </div>
                </div>
            )} */}
        </>
    );
};

export default Hero;
